import * as stylex from '@stylexjs/stylex'

import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import EmptyState from '../../components/EmptyState'
import PageHeader from '../../components/PageHeader'
import Icon from '../../components/Icon'
import { findPerson, formatActivityDate, formatMoney, groupSpent, type Balance, type Overview } from '../../lib/ledger'
import { mobileStyles as styles } from '../mobileStyles'

type DashboardMobilePageProps = {
    data: Overview
    onCreateGroup: () => void
    onOpenExpense: (expenseID: string) => void
    onOpenGroup: (groupID: string) => void
    onOpenPerson: (personID: string) => void
    onSettle: (balance: Balance) => void
}

const dashboardStyles = stylex.create({
    balanceLine: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', paddingBlock: '6px' },
    amountColumn: { display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'right', maxWidth: '48%' },
    currency: { fontSize: '11px', color: '#59665d' },
    amount: { whiteSpace: 'normal', overflowWrap: 'anywhere' },
    personButton: { display: 'flex', alignItems: 'center', gap: '10px', minHeight: '44px', minWidth: 0, textAlign: 'left' },
    balanceFields: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '10px',
        '@media (max-width: 420px)': {
            gridTemplateColumns: '1fr',
        },
    },
    sectionHeader: {
        display: 'flex',
        minWidth: 0,
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '4px',
    },
    settlementRow: {
        display: 'grid',
        width: '100%',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: '10px',
        borderBottomWidth: 1,
        borderBottomColor: '#edf0eb',
        paddingBlock: '12px',
        ':last-child': {
            borderBottomWidth: 0,
            paddingBottom: 0,
        },
    },
    settlementAmount: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '6px',
    },
    recentIcon: {
        display: 'flex',
        height: '38px',
        width: '38px',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '0.75rem',
        backgroundColor: '#edf6eb',
        color: '#468554',
    },
    groupArrow: {
        color: '#91a096',
    },
})

function greeting() {
    const hour = new Date().getHours()
    return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
}

export default function DashboardMobilePage({ data, onCreateGroup, onOpenExpense, onOpenGroup, onOpenPerson, onSettle }: DashboardMobilePageProps) {
    const currencies = Array.from(new Set([data.currentUser.currency, ...data.balances.map((balance) => balance.currency)]))
    const totals = currencies.map((currency) => {
        const balances = data.balances.filter((balance) => balance.currency === currency)
        return { currency, net: balances.reduce((sum, balance) => sum + balance.amountCents, 0), owed: balances.reduce((sum, balance) => sum + Math.max(0, balance.amountCents), 0), owing: balances.reduce((sum, balance) => sum + Math.max(0, -balance.amountCents), 0) }
    })
    const recent = [...data.expenses]
        .sort((a, b) => new Date(b.occurredAt ?? b.createdAt).getTime() - new Date(a.occurredAt ?? a.createdAt).getTime())
        .slice(0, 5)

    return (
        <div {...stylex.props(styles.page)}>
            <PageHeader
                eyebrow="Your overview"
                subtitle="Your shared money, kept simple and up to date."
                title={`${greeting()}, ${data.currentUser.name}`}
            />

            <section {...stylex.props(styles.card, styles.greenCard)}>
                <p {...stylex.props(styles.cardLabel, styles.greenCardLabel)}>Your net balance</p>
                {totals.map(({ currency, net }) => <div {...stylex.props(dashboardStyles.balanceLine)} key={currency}><p {...stylex.props(styles.cardValue)}>{formatMoney(Math.abs(net), currency)}</p><span>{net > 0 ? "You're owed" : net < 0 ? 'You owe' : 'Settled up'}</span></div>)}
            </section>

            <div {...stylex.props(dashboardStyles.balanceFields)}>
                <section {...stylex.props(styles.card)}>
                    <p {...stylex.props(styles.cardLabel)}>You are owed</p>
                    {totals.map(({ currency, owed }) => <p {...stylex.props(styles.cardValue, styles.positive)} key={currency}>{formatMoney(owed, currency)}</p>)}
                </section>
                <section {...stylex.props(styles.card)}>
                    <p {...stylex.props(styles.cardLabel)}>You owe</p>
                    {totals.map(({ currency, owing }) => <p {...stylex.props(styles.cardValue, styles.negative)} key={currency}>{formatMoney(owing, currency)}</p>)}
                </section>
            </div>

            <section {...stylex.props(styles.card)}>
                <div {...stylex.props(dashboardStyles.sectionHeader)}>
                    <div>
                        <h2 {...stylex.props(styles.cardTitle)}>Settle up</h2>
                        <p {...stylex.props(styles.muted)}>People with an active balance.</p>
                    </div>
                </div>
                {data.balances.length === 0 ? (
                    <EmptyState description="Everyone is settled up." icon={<Icon name="settle" />} />
                ) : (
                    <div>
                        {data.balances.slice(0, 5).map((balance) => {
                            const person = findPerson(data, balance.personId)
                            if (!person) return null
                            return (
                                <div {...stylex.props(dashboardStyles.settlementRow)} key={`${balance.personId}-${balance.currency}`}>
                                    <button {...stylex.props(dashboardStyles.personButton)} aria-label={`Open ${person.name}`} onClick={() => onOpenPerson(person.id)} type="button">
                                         <Avatar person={person} size={40} />
                                    <span {...stylex.props(styles.listMain)}>
                                        <p {...stylex.props(styles.listTitle)}>{person.name}</p>
                                        <p {...stylex.props(styles.listMeta)}>{balance.amountCents > 0 ? 'Owes you' : 'You owe'} · {balance.currency}</p>
                                    </span></button>
                                    <div {...stylex.props(dashboardStyles.settlementAmount)}>
                                        <p {...stylex.props(styles.listAmount, balance.amountCents > 0 ? styles.positive : styles.negative)}>{formatMoney(Math.abs(balance.amountCents), balance.currency)}</p>
                                        <Button aria-label={`Settle ${balance.currency} balance with ${person.name}`} onClick={() => onSettle(balance)} variant="secondary">Settle</Button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </section>

            <section {...stylex.props(styles.card)}>
                <div {...stylex.props(dashboardStyles.sectionHeader)}>
                    <div>
                        <h2 {...stylex.props(styles.cardTitle)}>Recent activity</h2>
                        <p {...stylex.props(styles.muted)}>What changed most recently.</p>
                    </div>
                    <Icon name="activity" />
                </div>
                {recent.length === 0 ? (
                    <EmptyState description="Your recent expenses will appear here." icon={<Icon name="receipt" />} />
                ) : (
                    <div {...stylex.props(styles.list)}>
                        {recent.map((expense) => (
                            <button {...stylex.props(styles.listItem)} key={expense.id} onClick={() => onOpenExpense(expense.id)} type="button">
                                <span {...stylex.props(dashboardStyles.recentIcon)}><Icon name="receipt" /></span>
                                <span {...stylex.props(styles.listMain)}>
                                    <span {...stylex.props(styles.listTitle)}>{expense.description}</span>
                                    <span {...stylex.props(styles.listMeta)}>{expense.notes || formatActivityDate(expense.occurredAt ?? expense.createdAt)}</span>
                                </span>
                                <span {...stylex.props(dashboardStyles.amountColumn)}><span {...stylex.props(styles.listAmount, dashboardStyles.amount)}>{formatMoney(expense.amountCents, expense.currency)}</span><Icon {...stylex.props(dashboardStyles.groupArrow)} name="chevron" /></span>
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section {...stylex.props(styles.card)}>
                <div {...stylex.props(dashboardStyles.sectionHeader)}>
                    <h2 {...stylex.props(styles.cardTitle)}>Your groups</h2>
                    <Button onClick={onCreateGroup} size="sm" variant="secondary">New group</Button>
                </div>
                {data.groups.slice(0, 3).map((group) => (
                    <button {...stylex.props(styles.listItem)} key={group.id} onClick={() => onOpenGroup(group.id)} type="button">
                        <span {...stylex.props(styles.emptyIcon)}><Icon name="groups" /></span>
                        <span {...stylex.props(styles.listMain)}>
                            <span {...stylex.props(styles.listTitle)}>{group.name}</span>
                            <span {...stylex.props(styles.listMeta)}>{group.memberIds.length} people · {formatMoney(groupSpent(data, group.id), data.currentUser.currency)}</span>
                        </span>
                        <Icon {...stylex.props(dashboardStyles.groupArrow)} name="arrow-right" />
                    </button>
                ))}
            </section>
        </div>
    )
}
